import React, { memo, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, Trash2, Copy, AlertTriangle } from 'lucide-react';
import { validateIfElseNode } from '../../utils/workflowValidation';

const IfElseNode = ({ data, isConnectable }) => {
  // Validate the node configuration
  const validation = useMemo(() => {
    // Custom validation for if-else since it uses branches not conditions
    const errors = [];
    const branches = data.branches || [];
    
    if (branches.length === 0) {
      errors.push('No branches configured');
    } else {
      // Check if at least one branch has conditions
      const hasConfiguredBranch = branches.some(b => 
        b.conditionGroups && b.conditionGroups.length > 0 &&
        b.conditionGroups.some(g => g.conditions && g.conditions.length > 0)
      );
      if (!hasConfiguredBranch) {
        errors.push('At least one branch needs conditions');
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }, [data.branches]);
  
  const hasErrors = !validation.isValid;

  const handleDoubleClick = () => {
    if (data.onConfigClick) {
      data.onConfigClick();
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data.onDelete && window.confirm('Delete this If/Else node?')) {
      data.onDelete();
    }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    if (data.onCopy) {
      data.onCopy();
    }
  };

  // Get branches from config, default to one branch + else
  const branches = useMemo(() => data.branches || [{ id: 'branch-1', name: 'Branch 1' }], [data.branches]);
  const elseEnabled = data.elseEnabled !== false; // Default to true
  
  // Calculate total outputs (branches + else if enabled)
  const totalOutputs = branches.length + (elseEnabled ? 1 : 0);

  // Get color based on index
  const getColorForIndex = (index) => {
    const colors = [
      '#22C55E', // green
      '#3B82F6', // blue
      '#F59E0B', // amber
      '#EF4444', // red
      '#8B5CF6', // violet
      '#EC4899', // pink
      '#14B8A6', // teal
      '#F97316', // orange
      '#6366F1', // indigo
      '#84CC16', // lime
    ];
    return colors[index % colors.length];
  };
  
  // Calculate positions for output handles
  const outputPositions = useMemo(() => {
    const positions = [];
    const spacing = 100 / (totalOutputs + 1);
    
    for (let i = 0; i < branches.length; i++) {
      positions.push({
        id: branches[i].id,
        name: branches[i].name || `Branch ${i + 1}`,
        left: `${spacing * (i + 1)}%`,
        color: getColorForIndex(i)
      });
    }
    
    if (elseEnabled) {
      positions.push({
        id: 'else',
        name: 'Else',
        left: `${spacing * (branches.length + 1)}%`,
        color: '#6B7280' // gray
      });
    }
    
    return positions;
  }, [branches, elseEnabled, totalOutputs]);

  // Count configured conditions
  const configuredCount = branches.filter(b => 
    b.conditionGroups && b.conditionGroups.length > 0 &&
    b.conditionGroups.some(g => g.conditions && g.conditions.length > 0)
  ).length;

  return (
    <div 
      className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-br from-purple-600 to-indigo-700 min-w-[240px] cursor-pointer hover:shadow-xl transition-shadow relative group ${
        hasErrors ? 'border-2 border-red-400 ring-2 ring-red-300' : 'border-2 border-purple-400'
      }`}
      onDoubleClick={handleDoubleClick}
      title={hasErrors ? `⚠️ ${validation.errors.join(', ')}` : 'Double-click to configure branches'}
    >
      {/* Validation Warning Badge */}
      {hasErrors && (
        <div className="absolute -top-3 -left-3 z-10 group/warning">
          <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-md animate-pulse">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          {/* Tooltip */}
          <div className="absolute left-8 top-0 w-48 bg-red-600 text-white text-xs rounded-lg px-3 py-2 shadow-lg opacity-0 invisible group-hover/warning:opacity-100 group-hover/warning:visible transition-all z-50">
            <div className="font-semibold mb-1">Configuration needed:</div>
            <ul className="list-disc list-inside">
              {validation.errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Copy Button */}
      <button
        onClick={handleCopy}
        className="absolute -top-2 -right-10 w-6 h-6 bg-blue-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600 flex items-center justify-center"
        title="Copy node"
      >
        <Copy className="w-3 h-3" />
      </button>
      
      {/* Delete Button */}
      <button
        onClick={handleDelete}
        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
        title="Delete node"
      >
        <Trash2 className="w-3 h-3" />
      </button>
      
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 !bg-white border-2 border-purple-500"
      />
      
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <GitBranch className="w-5 h-5 text-white" />
        <div className="text-sm font-bold text-white">If / Else</div>
      </div>
      
      {/* Label */}
      <div className="font-medium text-white text-sm mb-1">
        {data.label || 'Conditional Branches'}
      </div>
      
      {/* Branch Summary */}
      <div className="text-xs text-purple-200 mb-3">
        {branches.length} branch{branches.length !== 1 ? 'es' : ''} 
        {elseEnabled && ' + Else'}
        {configuredCount > 0 && (
          <span className="ml-2 text-green-300">
            ✓ {configuredCount} configured
          </span>
        )}
      </div>
      
      {/* Output Handles with Labels */}
      <div className="relative h-12 mt-2" style={{ minWidth: `${Math.max(totalOutputs * 70, 140)}px` }}>
        {outputPositions.map((output, index) => (
          <div 
            key={output.id}
            className="absolute flex flex-col items-center"
            style={{ 
              left: output.left, 
              transform: 'translateX(-50%)',
              bottom: 0
            }}
          >
            <span 
              className="text-[10px] text-white font-medium mb-1 whitespace-nowrap max-w-[60px] truncate"
              title={output.name}
            >
              {output.name}
            </span>
          </div>
        ))}
      </div>
      
      {/* React Flow Handles - positioned separately for proper detection */}
      {outputPositions.map((output, index) => (
        <Handle
          key={`handle-${output.id}`}
          type="source"
          position={Position.Bottom}
          id={output.id}
          isConnectable={isConnectable}
          className="w-3 h-3 border-2 border-white"
          style={{ 
            backgroundColor: output.color,
            left: output.left,
            transform: 'translateX(-50%)'
          }}
        />
      ))}
    </div>
  );
};

export default memo(IfElseNode);
